import { useEffect, useState } from "react";
import { Chevron } from "../../../shared/phone/Row";
import { Sheet } from "../../../shared/phone/Sheet";
import { usePhone } from "../../../shared/phone/media";
import { Icon } from "../../components/ui/Icon";
import { Inline } from "../../components/ui/Rich";
import { whenOf, type Changes, type Ver } from "../versions/Versions";
import { wordRuns, type Block, type LineChange } from "./parts";
import { libLoad, previousVer, verOf } from "./ShelfParts";
import type { ShelfItem } from "./shelf";
import { useCan } from "../../context/AuthContext";

/** Two versions to compare. */
export interface Pair {
  from: Ver;
  to: Ver;
}

type View = { at: "list" } | ({ at: "cmp"; ask: boolean } & Pair) | ({ at: "pick"; which: "from" | "to" } & Pair);

const SIGN = { add: "+", rm: "-", mod: "~" } as const;

function Words({ b }: { b: Block }) {
  if (b.t === "table") return <>{b.text.split("\n").filter((r) => !/^\|[\s\-:|]+\|$/.test(r)).join(" · ")}</>;
  return <Inline text={b.text} />;
}

function Changed({ c }: { c: LineChange }) {
  return (
    <p className={`lb-dl ${c.k}`}>
      <i aria-hidden="true">{SIGN[c.k]}</i>
      <span>
        {c.k === "add" ? (
          <Words b={c.b} />
        ) : c.k === "rm" ? (
          <Words b={c.a} />
        ) : (
          wordRuns(c.a.text, c.b.text).map((r, i) => (r.k === "same" ? <span key={i}>{r.t}</span> : r.k === "del" ? <del key={i}>{r.t}</del> : <ins key={i}>{r.t}</ins>))
        )}
      </span>
    </p>
  );
}

/**
 * An item's versions in a sheet: the list, newest first; then what changed between two of them, part by part;
 * then Keep or Update for a version that waits, or Use this version, asked first. Going back makes a new
 * version, so nothing is lost.
 */
export function VersionsSheet({
  it,
  start,
  busy,
  onUse,
  onKeep,
  onClose,
}: {
  it: ShelfItem;
  /** Open straight on What changed for these two; else on the list. */
  start: Pair | null;
  busy: boolean;
  onUse: (v: Ver) => void;
  onKeep: () => void;
  onClose: () => void;
}) {
  const phone = usePhone();
  const may = useCan()("editor");
  const [view, setView] = useState<View>(start ? { at: "cmp", ask: false, ...start } : { at: "list" });
  const [res, setRes] = useState<Changes | null>(null);
  const [err, setErr] = useState(false);
  const vers = it.versions.map(verOf).toReversed();
  const inUse = verOf(it.inUse);
  const waits = it.waits ? verOf(it.waits) : null;
  const nextYours = it.versions.filter((v) => v.who === "you").length + 1;

  const fromId = view.at === "list" ? "" : view.from.id;
  const toId = view.at === "list" ? "" : view.to.id;
  useEffect(() => {
    if (view.at === "list" || fromId === toId) return;
    let alive = true;
    setRes(null);
    setErr(false);
    libLoad(view.from, view.to).then(
      (r) => alive && setRes(r),
      () => alive && setErr(true),
    );
    return () => {
      alive = false;
    };
    // Why: the two ids are the comparison; the views around them are made afresh on each render.
  }, [fromId, toId]);

  const open = (v: Ver) => setView({ at: "cmp", ask: false, from: waits?.id === v.id ? inUse : (previousVer(it, v) ?? v), to: v });
  const done = (
    <button type="button" className="pn-tbtn strong" onClick={onClose}>
      Done
    </button>
  );

  if (view.at === "list")
    return (
      <Sheet open onClose={onClose} title="Versions" right={done}>
        <p className="lb-ssub">Newest first. Going back makes a new version, so nothing is lost.</p>
        <div className="pn-grp">
          {vers.map((v) => {
            const cur = v.id === inUse.id;
            const w = v.id === waits?.id;
            return (
              <button type="button" className="pn-row lb-vrow" key={v.id} onClick={() => open(v)}>
                <span className={["lb-vm", v.mine ? "y" : "", cur ? "on" : "", w ? "w" : ""].filter(Boolean).join(" ")} aria-hidden="true" />
                <span className="m">
                  <span className="t">
                    {v.label}
                    {cur ? <span className="pn-pill acc">In use</span> : w ? <span className="pn-pill warn">Waits for you</span> : null}
                  </span>
                  <span className="s">
                    {v.by} · {whenOf(v.at)}
                  </span>
                  {v.note ? <span className="lb-vn">{v.note}</span> : null}
                </span>
                <Chevron />
              </button>
            );
          })}
        </div>
      </Sheet>
    );

  if (view.at === "pick") {
    const cur = view[view.which];
    return (
      <Sheet
        open
        onClose={onClose}
        title={view.which === "from" ? "From" : "To"}
        left={
          <button type="button" className="pn-tbtn" onClick={() => setView({ ...view, at: "cmp", ask: false })}>
            Back
          </button>
        }
      >
        <div className="pn-grp">
          {vers.map((v) => (
            <button type="button" className="pn-row" key={v.id} onClick={() => setView({ at: "cmp", ask: false, from: view.which === "from" ? v : view.from, to: view.which === "to" ? v : view.to })}>
              <span className="m">
                <span className="t">{v.label}</span>
                <span className="s">{whenOf(v.at)}</span>
              </span>
              {v.id === cur.id ? (
                <span className="lb-tick">
                  <Icon name="check" />
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </Sheet>
    );
  }

  const { from, to } = view;
  const lines = res?.parts.reduce((n, p) => n + p.changes.length, 0) ?? 0;
  const foot = !may ? undefined : to.id === waits?.id ? (
    <>
      <div className="pn-btns">
        <button type="button" className="pn-btn" disabled={busy} onClick={onKeep}>
          Keep {inUse.label.toLowerCase()}
        </button>
        <button type="button" className="pn-btn pri" disabled={busy} onClick={() => onUse(to)}>
          Update to v{to.n}
        </button>
      </div>
      <p className="lb-fnote">Nothing updates until you say so.</p>
    </>
  ) : to.id !== inUse.id ? (
    view.ask ? (
      <>
        <p className="lb-ask">
          <b>Use the words of {to.label}?</b> They become Yours v{nextYours}, in use from now. Goals already running keep theirs. Nothing is deleted.
        </p>
        <div className="pn-btns">
          <button type="button" className="pn-btn" onClick={() => setView({ ...view, ask: false })}>
            Cancel
          </button>
          <button type="button" className="pn-btn pri" disabled={busy} onClick={() => onUse(to)}>
            Use these words
          </button>
        </div>
      </>
    ) : (
      <button type="button" className="pn-btn pri wide" onClick={() => setView({ ...view, ask: true })}>
        Use this version
      </button>
    )
  ) : undefined;

  return (
    <Sheet
      open
      onClose={onClose}
      tall={phone}
      title="What changed"
      left={
        <button type="button" className="pn-tbtn" onClick={() => setView({ at: "list" })}>
          Versions
        </button>
      }
      right={done}
      foot={foot}
    >
      <div className="pn-stack tight lb-cmp">
        <div className="pn-grp">
          <button type="button" className="pn-row" onClick={() => setView({ at: "pick", which: "from", from, to })}>
            <span className="m">
              <span className="t">From</span>
            </span>
            <span className="v">{from.label}</span>
            <Chevron />
          </button>
          <button type="button" className="pn-row" onClick={() => setView({ at: "pick", which: "to", from, to })}>
            <span className="m">
              <span className="t">To</span>
            </span>
            <span className="v">{to.label}</span>
            <Chevron />
          </button>
        </div>
        {from.id === to.id ? (
          <p className="lb-ssub">Pick two different versions.</p>
        ) : err ? (
          <p className="lb-ssub">Those versions did not load. Close this and try again.</p>
        ) : !res ? (
          <p className="lb-ssub">Reading both versions…</p>
        ) : res.parts.length === 0 ? (
          <p className="lb-ssub">The words are the same.</p>
        ) : (
          <>
            <div className="lb-dsum">
              <b>
                {res.parts.length} {res.parts.length === 1 ? "part" : "parts"} changed, {lines} {lines === 1 ? "line" : "lines"}
              </b>
              <span className="lb-k add">
                <i>+</i>Added
              </span>
              <span className="lb-k rm">
                <i>-</i>Taken out
              </span>
              <span className="lb-k mod">
                <i>~</i>Changed
              </span>
            </div>
            {res.parts.map((p) => (
              <div className="lb-dp" key={p.id}>
                <h4>
                  {p.title}
                  {p.status ? <span className="pn-pill">{p.status === "added" ? "New part" : "Taken out"}</span> : null}
                </h4>
                {p.changes.map((c, i) => (
                  <Changed key={i} c={c} />
                ))}
              </div>
            ))}
            {res.same > 0 ? (
              <p className="lb-ssub">
                {res.same} {res.same === 1 ? "part is" : "parts are"} the same.
              </p>
            ) : null}
          </>
        )}
      </div>
    </Sheet>
  );
}
